import { Button } from '@/components/ui/button';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { LayoutDashboard, Package, ClipboardList, Store, Inbox, MessageSquare, MoreHorizontal, type LucideIcon } from 'lucide-react';
import { markDirectNav } from '@/lib/missionReturn';
import { cn } from '@/lib/utils';
import type { MobileTabKey } from '@/lib/mobileTabs';
import { prefetchRoute, scheduleRouteWarmup } from '@/lib/routePrefetch';

const TABS: Record<MobileTabKey, { to: string; label: string; icon: LucideIcon }> = {
  desk: { to: '/desk', label: 'Desk', icon: LayoutDashboard },
  'my-listings': { to: '/my-listings', label: 'Inventory', icon: Package },
  'my-menu': { to: '/my-menu', label: 'Menus', icon: ClipboardList },
  'supplier-menus': { to: '/supplier-menus', label: 'Suppliers', icon: Store },
  'buy-requests': { to: '/buy-requests', label: 'Requests', icon: Inbox },
  messages: { to: '/messages', label: 'Messages', icon: MessageSquare },
};

interface Props {
  activeRoute: string;
  tabs: MobileTabKey[];
  badges: Partial<Record<MobileTabKey, boolean>>;
  deskLabel: string;
  buyRequestsTo: string;
  moreOpen: boolean;
  onMore: () => void;
}

/** Phones only: daily destinations in the thumb zone. The top bar keeps just the mark, assistant and bell. */
export function MobileTabBar({ activeRoute, tabs, badges, deskLabel, buyRequestsTo, moreOpen, onMore }: Props) {
  // Primitive dependency prevents entitlement/header rerenders restarting idle work.
  const warmPaths = tabs.filter((key) => key !== activeRoute).map((key) => TABS[key].to).join('|');
  useEffect(() => scheduleRouteWarmup(warmPaths ? warmPaths.split('|') : []), [warmPaths]);
  // Reserve room so the last row of any page is never hidden behind the bar.
  useEffect(() => {
    document.body.classList.add('has-mobile-tabbar');
    return () => document.body.classList.remove('has-mobile-tabbar');
  }, []);
  const tabInTabs = tabs.includes(activeRoute as MobileTabKey);
  // The active bubble wraps icon + label together (one capsule per tab).
  const itemBase = 'relative flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[52px] rounded-[22px] text-[10px] font-medium tracking-[0.01em] transition-colors duration-200';
  const item = (on: boolean) => cn(itemBase, on ? 'bg-foreground/[0.08] text-foreground' : 'text-muted-foreground');
  const pill = () => 'flex h-6 w-10 items-center justify-center';
  return (
    <nav aria-label="Main" onClickCapture={markDirectNav}
      className="md:hidden fixed inset-x-3 z-50 bottom-[calc(0.5rem+env(safe-area-inset-bottom))] rounded-[28px] border border-border/60 bg-background/75 backdrop-blur-xl backdrop-saturate-150 shadow-lg px-1.5 py-1">
      <div className="flex">
        {tabs.map((key) => {
          const t = TABS[key];
          const Icon = t.icon;
          const active = activeRoute === key;
          return (
            <Link key={key} to={key === 'buy-requests' ? buyRequestsTo : t.to} aria-current={active ? 'page' : undefined}
              aria-label={key === 'desk' ? deskLabel : t.label}
              onPointerDown={() => { void prefetchRoute(t.to); }}
              onFocus={() => { void prefetchRoute(t.to); }}
              onMouseEnter={() => { void prefetchRoute(t.to); }}
              className={item(active)}>
              <span className={cn('relative', pill())}>
                <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} aria-hidden />
                {badges[key] && <span className="absolute top-0.5 right-2.5 circle-2 bg-destructive ring-2 ring-background" aria-hidden />}
              </span>
              {t.label}
            </Link>
          );
        })}
        {(() => { const on = moreOpen || (!tabInTabs && activeRoute !== 'none'); return (
        <Button variant="quiet" size="content" type="button" onClick={onMore} aria-expanded={moreOpen} aria-label="More"
          className={cn("p-0 [&_svg]:size-5", item(on))}>
          <span className={cn('relative', pill())}>
            <MoreHorizontal className="h-5 w-5" aria-hidden />
            {tabs.every((k) => k !== 'buy-requests') && (badges['buy-requests'] || badges['supplier-menus']) && <span className="absolute top-0.5 right-2.5 circle-2 bg-destructive ring-2 ring-background" aria-hidden />}
          </span>
          More
        </Button>); })()}
      </div>
    </nav>
  );
}
